// ═══════════════════════════════════════════════════════════════════════════
// DISTIL PROTOCOL - BADGE MANAGER
// Handles 8-tier badge progression + 4 Toilet Mode exclusive badges
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// BADGE TIERS (8 tiers, purity score based)
// NOTE: These use "threshold" to match the existing constants.js BADGE_TIERS
//       so we can use either system. This manager works off purity score.
// ═══════════════════════════════════════════════════════════════════════════

export const BADGE_TIER_DATA = [
  {
    tier: 0,
    name: 'Novice',
    minScore: 0,
    maxScore: 49,
    emoji: '🌱',
    color: '#9ca3af',
    glowColor: 'rgba(156, 163, 175, 0.4)',
    description: 'Just getting started',
  },
  {
    tier: 1,
    name: 'Apprentice',
    minScore: 50,
    maxScore: 199,
    emoji: '⚙️',
    color: '#60a5fa',
    glowColor: 'rgba(96, 165, 250, 0.4)',
    description: 'Learning the ropes',
  },
  {
    tier: 2,
    name: 'Adept',
    minScore: 200,
    maxScore: 499,
    emoji: '⚡',
    color: '#8b5cf6',
    glowColor: 'rgba(139, 92, 246, 0.4)',
    description: 'Getting the hang of it',
  },
  {
    tier: 3,
    name: 'Expert',
    minScore: 500,
    maxScore: 999,
    emoji: '💎',
    color: '#22d3ee',
    glowColor: 'rgba(34, 211, 238, 0.4)',
    description: 'Purification master',
  },
  {
    tier: 4,
    name: 'Master',
    minScore: 1000,
    maxScore: 2499,
    emoji: '👑',
    color: '#a855f7',
    glowColor: 'rgba(168, 85, 247, 0.4)',
    description: 'Elite purifier',
  },
  {
    tier: 5,
    name: 'Elite',
    minScore: 2500,
    maxScore: 4999,
    emoji: '🔥',
    color: '#f59e0b',
    glowColor: 'rgba(245, 158, 11, 0.4)',
    description: 'Top tier performance',
  },
  {
    tier: 6,
    name: 'Legendary',
    minScore: 5000,
    maxScore: 9999,
    emoji: '⚔️',
    color: '#ef4444',
    glowColor: 'rgba(239, 68, 68, 0.4)',
    description: 'Legendary status achieved',
  },
  {
    tier: 7,
    name: 'Mythic',
    minScore: 10000,
    maxScore: Infinity,
    emoji: '🌟',
    color: '#FFD700',
    glowColor: 'rgba(255, 215, 0, 0.5)',
    description: 'Absolute legend',
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// TOILET MODE EXCLUSIVE BADGES
// ═══════════════════════════════════════════════════════════════════════════

export const TOILET_BADGES = [
  {
    id: 'toilet_warrior',
    name: 'Toilet Warrior',
    emoji: '🚽',
    requirement: 'Hold 287K+ TOILET tokens',
    requirementKey: 'isToiletHolder',
    color: '#FFD700',
    description: 'You hold the sacred $TOILET token. You are one of us.',
  },
  {
    id: 'flush_master',
    name: 'Flush Master',
    emoji: '💪',
    requirement: 'Complete 100 purifications in Toilet Mode',
    requirementKey: 'toiletModePurifications',
    requirementValue: 100,
    color: '#FFD700',
    description: '100 flushes. The filth fears you.',
  },
  {
    id: 'spin_champion',
    name: 'Spin Champion',
    emoji: '🎰',
    requirement: 'Win 10+ SpinWheel rewards',
    requirementKey: 'spinWheelWins',
    requirementValue: 10,
    color: '#FFD700',
    description: 'The wheel knows your name.',
  },
  {
    id: 'frenzy_king',
    name: 'Frenzy King',
    emoji: '⚡',
    requirement: 'Score 1000+ in Flush Frenzy',
    requirementKey: 'flushFrenzyHighScore',
    requirementValue: 1000,
    color: '#FFD700',
    description: 'Unmatched in the Frenzy. Absolute royalty.',
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// CORE BADGE FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get current badge based on purity score
 * @param {number} purityScore
 * @returns {Object} Badge tier data
 */
export function getCurrentBadge(purityScore = 0) {
  // Walk tiers in reverse to find the highest qualifying tier
  for (let i = BADGE_TIER_DATA.length - 1; i >= 0; i--) {
    if (purityScore >= BADGE_TIER_DATA[i].minScore) {
      return BADGE_TIER_DATA[i];
    }
  }
  return BADGE_TIER_DATA[0]; // Fallback to Novice
}

/**
 * Get next badge tier (null if already Mythic)
 * @param {number} purityScore
 * @returns {Object|null} Next badge tier data or null if maxed
 */
export function getNextBadge(purityScore = 0) {
  const current = getCurrentBadge(purityScore);
  if (current.tier >= BADGE_TIER_DATA.length - 1) return null;
  return BADGE_TIER_DATA[current.tier + 1];
}

/**
 * Get progress percentage to next tier (0-100)
 * @param {number} purityScore
 * @returns {number} 0-100 progress percentage
 */
export function getProgressToNextTier(purityScore = 0) {
  const current = getCurrentBadge(purityScore);
  const next = getNextBadge(purityScore);

  // Max tier — always 100%
  if (!next) return 100;

  const scoreIntoTier = purityScore - current.minScore;
  const tierRange = next.minScore - current.minScore;

  return Math.min(100, Math.floor((scoreIntoTier / tierRange) * 100));
}

/**
 * Get score needed to reach next tier
 * @param {number} purityScore
 * @returns {number} Score needed, or 0 if maxed
 */
export function getScoreToNextTier(purityScore = 0) {
  const next = getNextBadge(purityScore);
  if (!next) return 0;
  return Math.max(0, next.minScore - purityScore);
}

/**
 * Determine which Toilet Mode badges a user has earned
 * @param {Object} userStats - { isToiletHolder, toiletModePurifications, spinWheelWins, flushFrenzyHighScore }
 * @returns {Array} Array of earned TOILET_BADGES objects
 */
export function getEarnedToiletBadges(userStats = {}) {
  return TOILET_BADGES.filter(badge => {
    if (badge.requirementKey === 'isToiletHolder') {
      return !!userStats.isToiletHolder;
    }
    const userValue = userStats[badge.requirementKey] || 0;
    return userValue >= (badge.requirementValue || 1);
  });
}

/**
 * Get complete badge summary for a user
 * @param {number} purityScore
 * @param {Object} userStats - Toilet mode stats
 * @returns {Object} Complete badge data
 */
export function getAllBadges(purityScore = 0, userStats = {}) {
  const currentBadge = getCurrentBadge(purityScore);
  const nextBadge = getNextBadge(purityScore);
  const progress = getProgressToNextTier(purityScore);
  const scoreNeeded = getScoreToNextTier(purityScore);
  const isMaxTier = !nextBadge;
  const toiletBadges = getEarnedToiletBadges(userStats);

  return {
    currentBadge,
    nextBadge,
    progress,
    scoreNeeded,
    isMaxTier,
    toiletBadges,
    allTiers: BADGE_TIER_DATA,
  };
}

export default {
  BADGE_TIER_DATA,
  TOILET_BADGES,
  getCurrentBadge,
  getNextBadge,
  getProgressToNextTier,
  getScoreToNextTier,
  getEarnedToiletBadges,
  getAllBadges,
};
